import React, {
  useCallback, useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Carousel } from "react-responsive-carousel";
import { Box, Dialog,IconButton, Slide, Typography } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import dayjs from 'dayjs';
import {
  createSchedule,
  getSchedules,
  updateSchedule,
} from "src/store/reducers/schedulesSlice";
import {
  getNormalizedDaysOff,
  getNormalizedHolidays,
  getNormalizedWorkingTimes,
} from "src/utils/calendar";
import { generalStyles } from "src/utils/generalStyles/generalStyles";
import { isAddress } from "src/utils/validation";

import { CloseIcon } from "../../../Icons";
import {
  closeWorkingTimeDialog,
  resetWorkingTimeDialogParams,
} from "../../../store/reducers/dialogSlice";
import { closeModal, showModal } from "../../../store/reducers/modalSlice";
import { Calendar,CalendarSideBar } from "../..";
import { SubmitButton } from "../../Button/SubmitButton";
import { DefaultInput } from "../../Input/DefaultInput";
import { Steppler } from "../../Steppler/Steppler";

const Transition = React.forwardRef((
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

const steps = [
  {
    title: "Template information",
  },
  {
    title: "Schedule template",
  },
];

export const WorkingTimeDialog = ({ isOpen }: any) => {
  const dispatch = useDispatch();

  const { workingTimeDialogParams: params } = useSelector(
    (state: any) => state.dialog
  );

  const [activeStep, setActiveStep] = useState<number>(+params?.step);
  const [maxAvailableStepToClick, setMaxAvailableStepToClick] =
    useState<number>(params.maxAvailableStep);
  const formRef = useRef<any>();

  // Step 1 values
  const [title, setTitle] = useState<string>(params.data.title);
  const [description, setDescription] = useState<string>(
    params.data.description
  );

  // Step 2 values
  const [workingTimes, setWorkingTimes] = useState(params.data.workingTimes);
  const [holidays, setHolidays] = useState(params.data.holidays);
  const [daysOff, setDaysOff] = useState(params.data.daysOff);
  const [selectorId, setSelectorId] = useState();

  const period = useMemo(() => {
    return workingTimes ? workingTimes[0]?.period : {}
  }, [workingTimes])

  const handleChangeTitle = useCallback((newValue: string) => {
    setTitle(newValue);
  },[]);

  const handleChangeDescription = useCallback((newValue: string) => {
    setDescription(newValue);
  },[]);

  const handleChangeWorkingTime = useCallback((newWorkingTimes:any) => {
    setWorkingTimes(newWorkingTimes);
  },[])

  const handleChangeDaysOff = useCallback((newDaysOff:any) => {
    setDaysOff(newDaysOff);
  },[])

  const handleChangeHolidays = useCallback((newHolidays:any) => {
    setHolidays(newHolidays);
  },[])

  const handleChangeSelectorId = useCallback((newSelectorId:any) => {
    setSelectorId(newSelectorId);
  },[])

  const isContinueButtonAvailable = useMemo(() => {
    return isAddress(title) && isAddress(description);
  }, [title, description]);

  const isSaveButtonValid = useMemo(() => {
    return (
      isContinueButtonAvailable &&
      workingTimes.length
    );
  }, [isContinueButtonAvailable, workingTimes]);

  const handleChangeStep = (nextStep: number) => {
    setActiveStep(nextStep);
  };

  const handleContinueButtonPress = () => {
    setActiveStep((oldStep) => oldStep + 1);
    setMaxAvailableStepToClick(activeStep + 1);
  };

  const resetDialogData = () => {
    setTitle("");
    setDescription("");
    setWorkingTimes([]);
    setHolidays([]);
    setDaysOff([]);
    setActiveStep(0);
    setMaxAvailableStepToClick(0);
  };

  const onClose = () => {
    resetDialogData();
    dispatch(resetWorkingTimeDialogParams());
    dispatch(closeWorkingTimeDialog());
  };

  const handleSaveButtonPress = () => {
    const normalizedWorkingTimes = getNormalizedWorkingTimes(workingTimes);
    const normalizedDaysOff = getNormalizedDaysOff(daysOff);
    const normalizedHolidays = getNormalizedHolidays(holidays);

    if (params.isEdit) {
      dispatch(
        updateSchedule({
          title,
          description,
          workingTimes: normalizedWorkingTimes,
          daysOff: normalizedDaysOff,
          holidays: normalizedHolidays,
          currentId: params.data.id,
          onSuccess: () => {
            dispatch(
              showModal({
                modalType: "SUCCESS",
                successText: "Changes successfully saved",
                okButtonTitle: "Ok, I got it",
                onButtonClick: () => {
                  dispatch(getSchedules());
                  dispatch(closeModal());
                  onClose();
                },
              })
            );
          },
          onFailure: (error: any) => {
            dispatch(
              showModal({
                modalType: "DELETE",
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: "Ok, I got it",
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          },
        })
      );
    } else {
      dispatch(
        createSchedule({
          title,
          description,
          workingTimes: normalizedWorkingTimes,
          daysOff: normalizedDaysOff,
          holidays: normalizedHolidays,
          onSuccess: () => {
            dispatch(
              showModal({
                modalType: "SUCCESS",
                successText: `Schedule template ${title} successfully created`,
                okButtonTitle: "Ok, I got it",
                onButtonClick: () => {
                  dispatch(getSchedules());
                  dispatch(closeModal());
                  onClose();
                },
              })
            );
          },
          onFailure: (error: any) => {
            dispatch(
              showModal({
                modalType: "DELETE",
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: "Ok, I got it",
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          },
        })
      );
    }
  };

  useEffect(() => {
    // todo little hack for scroll to top after every change active step carousel
    //@ts-ignore
    window.document.querySelector('.MuiPaper-root')?.scrollTo(0, 0);
  }, [activeStep]);

  return (
    <Dialog
      fullScreen
      open={isOpen}
      onClose={onClose}
      disableEscapeKeyDown
      TransitionComponent={Transition}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          width: "100%",
          backgroundColor: "#fafafa",
          padding: "16px 40px",
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: "100%",
            marginBottom: "16px",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              backgroundColor: "#F3F3F3",
              color: "#676767",
              fontSize: "26px",
            }}
            disableRipple
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
          <Box>
            <Typography
              sx={{
                fontSize: "26px",
                fontWeight: "600",
              }}
            >
              {params.isEdit ? "Edit schedule" : "Add new schedule"}
            </Typography>
          </Box>

          {activeStep < steps.length - 1 ? (
            <SubmitButton
              title="Continue"
              disabled={!isContinueButtonAvailable}
              onClick={handleContinueButtonPress}
            />
          ) : (
            <SubmitButton
              title={params.isEdit ? "Save changes" : "Create schedule"}
              disabled={!isSaveButtonValid}
              onClick={handleSaveButtonPress}
            />
          )}
        </Box>

        <Steppler
          steps={steps}
          maxAvailableStepToClick={maxAvailableStepToClick}
          activeStep={activeStep}
          onStepChange={handleChangeStep}
        />
      </Box>
      <Carousel
        selectedItem={activeStep}
        showArrows={false}
        showStatus={false}
        showIndicators={false}
        showThumbs={false}
        transitionTime={120}
      >
        {/* Step 1 = Basic Information*/}
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                padding: "0px 16px 0",
                width: "761px",
                minWidth: "400px",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: "600",
                    marginBottom: "20px",
                    marginTop: "60px",
                  }}
                >
                  Basic information
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                }}
              >
                <form ref={formRef}>
                  <DefaultInput
                    initialValue={title}
                    placeholder="name"
                    label="Template name"
                    onInputValueChange={handleChangeTitle}
                    validationFn={isAddress}
                    sx={generalStyles.borderTopRightLeftRadius}
                  />
                  <DefaultInput
                    initialValue={description}
                    label="Additional name"
                    placeholder="Additional name"
                    onInputValueChange={handleChangeDescription}
                    validationFn={isAddress}
                    sx={generalStyles.borderBottomLeftRightRadius}
                  />
                </form>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* Step 2 = Schedule template*/}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              padding: "0px",
              width: "100%",
            }}
          >
            <CalendarSideBar
              period={period}
              workingTimes={workingTimes}
              onWorkingTimesChange={handleChangeWorkingTime}
              daysOff={daysOff}
              onDaysOffChange={handleChangeDaysOff}
              holidays={holidays}
              onHolidaysChange={handleChangeHolidays}
              periodId={selectorId}
              onChangePeriodId={handleChangeSelectorId}
              lastUpdated={params.data?.holidays?.length === holidays?.length ? params.data.updatedAt : dayjs().toISOString()}
            />
            <Calendar
              workingTimes={workingTimes}
              holidays={holidays}
              daysOff={daysOff}
            />
          </Box>
        </Box>
      </Carousel>
    </Dialog>
  );
};
